import * as ko from 'knockout';
import { find } from 'lodash';

import { deleteAccount } from '@pressreader/account-profile';
import { localizedString } from '@pressreader/resources';
import { GdprConsentType, tryDecomposeConsent } from '@pressreader/src/account/models/gdprcontext';
import ndConfig from '@pressreader/src/deprecated/nd.config';
import alert from '@pressreader/src/nd.alert';
import ndUiProgress from '@pressreader/src/nd.ui.progress';
import { logAndAlertError } from '@pressreader/src/shared/errors/error.handlers';

import ndLogger from 'nd.logger';
import res from 'nd.res';
import ndAuthentication from 'authentication/nd.authentication';
import { getCurrentSubscription } from 'subscriptions/services';

import { getCurrentUserConsents, updateCurrentUserConsent, userConsentsUpdates } from '../services/gdpr.service';

class ConsentSectionViewModel {
    termsOfUseGranted: KnockoutObservable<boolean>;
    cookiesGranted: KnockoutObservable<boolean>;
    isAccountDeleteAllowed: KnockoutComputed<Promise<boolean>>;
    consentsUpdatesSubscription;

    constructor() {
        this.termsOfUseGranted = ko.observable(false);
        this.cookiesGranted = ko.observable(false);

        this.consentsUpdatesSubscription = userConsentsUpdates.subscribe(() => {
            this.load().catch(ndLogger.error);
        });

        this.isAccountDeleteAllowed = ko
            .pureComputed(() => ndConfig.loaded().then(() => ndConfig.get('features.account.delete', false)))
            .extend({ async: true });

        this.load().catch(ndLogger.error);
    }

    async deleteAccount() {
        try {
            const subscription = await getCurrentSubscription();
            if (subscription?.subscriptionMonthlyFee !== 0) {
                await alert().confirm(localizedString('Accounting.Management.Account.Delete.NonZeroBalanceWarning'), null, null, null, 'html');
            }

            await alert().confirm(localizedString('Accounting.Management.Account.Delete.ConfirmationMessage'), null, null, null, 'html');

            await alert().confirm(localizedString('Accounting.Management.Account.Delete.DoubleCheckConfirmationMessage'), null, null, null, 'html');

            await ndUiProgress.show();

            await deleteAccount();

            await this.signOut();
        } catch (error) {
            logAndAlertError(error);
        } finally {
            ndUiProgress.hide();
        }
    }

    public revokeCookiesConsent(): void {
        res.valAsync('Accounting.Management.Consent.Cookies.Revoke.ConfirmationMessage')
            .then(message => alert().confirm(message))
            .then(ndUiProgress.show)
            .then(() => updateCurrentUserConsent(GdprConsentType.Cookies, false))
            .then(() => this.signOut())
            .catch(logAndAlertError)
            .finally(ndUiProgress.hide);
    }

    public revokeUserAgreement(): void {
        res.valAsync('Accounting.Management.Consent.Agreement.Revoke.ConfirmationMessage')
            .then(message => alert().confirm(message))
            .then(ndUiProgress.show)
            .then(() => Promise.all(tryDecomposeConsent(GdprConsentType.TermsOfUse).map(x => updateCurrentUserConsent(x, false))))
            .then(() => this.signOut())
            .catch(logAndAlertError)
            .finally(ndUiProgress.hide);
    }

    private load(): Promise<void> {
        const consentTypeAttribute = 'consentType';
        const isGrantedAttribute = 'isGranted';

        return getCurrentUserConsents().then(consents => {
            const termsOfUse = find(consents, c => c[consentTypeAttribute] === GdprConsentType.TermsOfUse);
            const cookies = find(consents, c => c[consentTypeAttribute] === GdprConsentType.Cookies);

            this.termsOfUseGranted(termsOfUse && termsOfUse[isGrantedAttribute]);
            this.cookiesGranted(cookies && cookies[isGrantedAttribute]);
        });
    }

    private signOut() {
        ndAuthentication.signOut();
    }
}

export default ConsentSectionViewModel;
